import type {
  AbhaSuccessContentBlock,
  AbhaSuccessPanelData,
  AbhaSuccessPanelPlatformData,
} from '@components/NewProduct/AbhaHeroWidget/steps/abhaSuccessRightPanelTypes'

type NormalizeSuccessPanelDefaults = {
  title: string
  appLink: string
}

const toFeaturesItems = (items: AbhaSuccessPanelPlatformData['items']) =>
  (items ?? [])
    .filter((item): item is { icon: string; text: string } => Boolean(item?.icon && item?.text))
    .map(item => ({ icon: item.icon, text: item.text }))

const buildLegacyContent = (
  platformData: AbhaSuccessPanelPlatformData,
  defaults: NormalizeSuccessPanelDefaults,
): AbhaSuccessContentBlock[] => {
  const content: AbhaSuccessContentBlock[] = []

  if (platformData.cardData || platformData.defaultAppLink) {
    content.push({
      type: 'qr_card',
      title: platformData.cardData?.title,
      subTitle: platformData.cardData?.subTitle,
      description: platformData.cardData?.description,
      hasQr: true,
      appLink: platformData.defaultAppLink ?? defaults.appLink,
    })
  }

  const featureItems = toFeaturesItems(platformData.items)

  if (featureItems.length > 0) {
    content.push({
      type: 'features',
      items: featureItems,
    })
  }

  if (platformData.ctaText) {
    content.push({
      type: 'footer_button',
      ctaText: platformData.ctaText,
      action: 'download_app',
    })
  }

  return content
}

export const normalizeSuccessPanelData = (
  platformData: AbhaSuccessPanelPlatformData | undefined,
  defaults: NormalizeSuccessPanelDefaults,
): AbhaSuccessPanelData => {
  if (!platformData) {
    return {
      panelVariant: 'plain',
      header: { title: defaults.title },
      content: [],
    }
  }

  const panelVariant = platformData.panelVariant === 'themed' ? 'themed' : 'plain'
  const header = {
    badgeText: platformData.header?.badgeText ?? platformData.badgeText,
    title: platformData.header?.title ?? platformData.title ?? defaults.title,
    description: platformData.header?.description ?? platformData.description,
  }

  const content = Array.isArray(platformData.content)
    ? platformData.content
    : buildLegacyContent(platformData, defaults)

  return {
    panelVariant,
    header,
    content,
  }
}
